/**
 * Image Helper Utilities for AI Service
 */

import { APPENV } from "@/config/env";
import { MiddlewareAPI } from "@/lib/axios";
import { Obj } from "@/types";

// Export debug utilities
export { debugImageFetch } from './debugImageFetch';
export { debugBackendImageFetch } from "./debugBackendImage";


/**
 * Build a fully transparent 1x1 PNG File — used as a positional placeholder
 * for a multi-frame sash overlay call when a given frame has no grill
 * selected at all (both top and bottom sash are "None"). The multi-frame
 * endpoint zips top_grids[]/bottom_grids[] positionally against the window's
 * physical frames left-to-right, so a frame with no grill still needs an
 * entry in the array to keep every other frame's position correct — an
 * empty/transparent image overlays nothing visible.
 */
export const createTransparentPngFile = (filename: string = "blank_grid.png"): File => {
  const canvas = document.createElement("canvas");
  canvas.width = 1;
  canvas.height = 1;
  const ctx = canvas.getContext("2d");
  if (ctx) ctx.clearRect(0, 0, 1, 1);
  return dataURLtoFile(canvas.toDataURL("image/png"), filename);
};

/**
 * Fetch product image from backend API and convert to File object
 * @param productGuid - Product GUID
 * @param fileName - Image file name
 * @returns Promise<File>
 */
export const fetchProductImageFromBackend = async (
  productGuid: string,
  fileName: string,
): Promise<File> => {
  try {


    // Route through MiddlewareAPI (not a raw fetch) so the Bearer token
    // interceptor in src/lib/axios.ts is applied — the backend now requires it.
    const response = await MiddlewareAPI.get("/GetProductImageFile", {
      params: { productGuid, fileName },
      responseType: "arraybuffer",
    });


    // CRITICAL: Keep the response as arrayBuffer to preserve binary data
    const arrayBuffer = response.data as ArrayBuffer;

    // Get content type from headers, fallback to detecting from magic bytes
    const responseContentType = response.headers["content-type"] as string | undefined;

    // Create blob from arrayBuffer (not from response.blob() which might apply encoding)
    let blob = new Blob([arrayBuffer], {
      type: responseContentType || "application/octet-stream",
    });


    // We already have arrayBuffer, just create Uint8Array from it
    const uint8Array = new Uint8Array(arrayBuffer);


    // Detect file type from magic bytes (file signature)
    let mimeType = "image/png";
    if (
      uint8Array[0] === 0x89 &&
      uint8Array[1] === 0x50 &&
      uint8Array[2] === 0x4e &&
      uint8Array[3] === 0x47
    ) {
      mimeType = "image/png";
    } else if (
      uint8Array[0] === 0xff &&
      uint8Array[1] === 0xd8 &&
      uint8Array[2] === 0xff
    ) {
      mimeType = "image/jpeg";
    } else if (
      uint8Array[0] === 0x52 &&
      uint8Array[1] === 0x49 &&
      uint8Array[2] === 0x46 &&
      uint8Array[3] === 0x46
    ) {
      // RIFF header (WEBP)
      mimeType = "image/webp";
    } else if (
      uint8Array[0] === 0x47 &&
      uint8Array[1] === 0x49 &&
      uint8Array[2] === 0x46
    ) {
      // GIF87a or GIF89a
      mimeType = "image/gif";
    } else if (
      blob.type &&
      blob.type !== "application/octet-stream" &&
      blob.type !== ""
    ) {
      // Trust blob type if valid and we couldn't detect from magic bytes
      mimeType = blob.type;
    } else {
      // Fallback to fileName extension
      const fileNameLower = fileName.toLowerCase();
      if (fileNameLower.endsWith(".png")) {
        mimeType = "image/png";
      } else if (fileNameLower.match(/\.jpe?g$/i)) {
        mimeType = "image/jpeg";
      } else if (fileNameLower.endsWith(".webp")) {
        mimeType = "image/webp";
      } else if (fileNameLower.endsWith(".gif")) {
        mimeType = "image/gif";
      } else if (fileNameLower.endsWith(".svg")) {
        mimeType = "image/svg+xml";
      } else {
        mimeType = "image/png";
      }
    }

    // Re-create blob with proper type from the validated array buffer
    blob = new Blob([uint8Array], { type: mimeType });

    // Validate blob before creating File
    if (!blob || blob.size === 0) {
      throw new Error(`Invalid blob: size is ${blob?.size || 0} bytes`);
    }

    // Warning: Check if this might be a placeholder image
    // Most placeholder images are small (< 10KB), while real product images are typically larger
    if (blob.size < 10240 && mimeType === "image/png") {
    }

    // Create File with explicit type
    const file = new File([blob], fileName, {
      type: mimeType,
      lastModified: Date.now(),
    });

    // Final validation

    if (file.size === 0) {
      throw new Error(
        "File created but size is 0 bytes - blob may be corrupted",
      );
    }

    if (!file.type || file.type === "application/octet-stream") {
    }

    return file;
  } catch (error) {
    throw new Error(
      `Failed to fetch product image from backend API. Error: ${error instanceof Error ? error.message : String(error)}`,
    );
  }
};

/**
 * Fetch image URL and convert to Blob URL to prevent corruption
 * Use this for AI result images or any image that needs binary-safe fetching
 * @param imageUrl - Image URL to fetch
 * @returns Promise<string> - Blob URL that can be used in <img src>
 */
export const fetchImageAsBlobUrl = async (
  imageUrl: string,
): Promise<string> => {
  try {

    // Fetch image using arrayBuffer to prevent binary corruption
    const response = await fetch(imageUrl, {
      method: "GET",
      mode: "cors",
      cache: "no-cache",
      credentials: "omit",
    });


    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    // CRITICAL: Get as arrayBuffer to preserve binary data
    const arrayBuffer = await response.arrayBuffer();

    // Detect MIME type from magic bytes
    const uint8Array = new Uint8Array(arrayBuffer);

    let mimeType = response.headers.get("content-type") || "image/png";

    // Verify with magic bytes
    if (
      uint8Array[0] === 0x89 &&
      uint8Array[1] === 0x50 &&
      uint8Array[2] === 0x4e &&
      uint8Array[3] === 0x47
    ) {
      mimeType = "image/png";
    } else if (
      uint8Array[0] === 0xff &&
      uint8Array[1] === 0xd8 &&
      uint8Array[2] === 0xff
    ) {
      mimeType = "image/jpeg";
    } else if (
      uint8Array[0] === 0x52 &&
      uint8Array[1] === 0x49 &&
      uint8Array[2] === 0x46 &&
      uint8Array[3] === 0x46
    ) {
      mimeType = "image/webp";
    }

    // Create blob with correct MIME type
    const blob = new Blob([arrayBuffer], { type: mimeType });

    // Create blob URL
    const blobUrl = URL.createObjectURL(blob);

    return blobUrl;
  } catch (error) {
    throw new Error(
      `Failed to fetch image: ${error instanceof Error ? error.message : String(error)}`,
    );
  }
};

/**
 * Convert a URL to a File object
 * @param url - Image URL (can be remote URL or data URL)
 * @param filename - Desired filename for the file
 * @returns Promise<File>
 */
export const urlToFile = async (
  url: string,
  filename: string = "image.png"
): Promise<File> => {
  try {
    // Handle data URLs (base64)
    if (url.startsWith("data:")) {
      return dataURLtoFile(url, filename);
    }

    // Device-local cache path (e.g. a mobile app's template-cache) — this file
    // lives on whatever device originally cached it, not wherever this code is
    // running now, so it can never be fetched here. Fail fast and clearly
    // instead of letting a `file://` fetch hang or fail with an opaque CORS
    // error — callers already fall back to the plain (unblended) template on error.
    if (url.startsWith("file://")) {
      throw new Error(
        `Cannot fetch a device-local file path from this context: ${url}. ` +
        `This image's stored URL is a local cache path from another device rather than a hosted URL.`
      );
    }

    // Try different fetch strategies for CORS-restricted images
    let blob: Blob | null = null;
    let mimeType = "image/png";

    try {
      // Strategy 1: Try with CORS mode first
      const response = await fetch(url, {
        mode: "cors",
        cache: "no-cache"
      });


      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      blob = await response.blob();


      // CRITICAL FIX: Read blob data to determine actual file type from magic bytes
      const arrayBuffer = await blob.arrayBuffer();
      const uint8Array = new Uint8Array(arrayBuffer);


      // Detect file type from magic bytes (file signature)
      if (uint8Array[0] === 0x89 && uint8Array[1] === 0x50 && uint8Array[2] === 0x4E && uint8Array[3] === 0x47) {
        mimeType = 'image/png';
      } else if (uint8Array[0] === 0xFF && uint8Array[1] === 0xD8 && uint8Array[2] === 0xFF) {
        mimeType = 'image/jpeg';
      } else if (uint8Array[0] === 0x52 && uint8Array[1] === 0x49 && uint8Array[2] === 0x46 && uint8Array[3] === 0x46) {
        // RIFF header (WEBP)
        mimeType = 'image/webp';
      } else if (uint8Array[0] === 0x47 && uint8Array[1] === 0x49 && uint8Array[2] === 0x46) {
        // GIF87a or GIF89a
        mimeType = 'image/gif';
      } else if (blob.type && blob.type !== 'application/octet-stream' && blob.type !== '') {
        // Trust blob type if valid and we couldn't detect from magic bytes
        mimeType = blob.type;
      } else {
        // Fallback to URL extension
        const urlLower = url.toLowerCase();
        if (urlLower.endsWith('.png')) {
          mimeType = 'image/png';
        } else if (urlLower.match(/\.jpe?g$/i)) {
          mimeType = 'image/jpeg';
        } else if (urlLower.endsWith('.webp')) {
          mimeType = 'image/webp';
        } else if (urlLower.endsWith('.gif')) {
          mimeType = 'image/gif';
        } else if (urlLower.endsWith('.svg')) {
          mimeType = 'image/svg+xml';
        } else {
          mimeType = 'image/png';
        }
      }

      // Re-create blob with proper type from the validated array buffer
      blob = new Blob([uint8Array], { type: mimeType });
    } catch (corsError) {

      // Strategy 2: Try using an img element to load the image
      try {
        blob = await loadImageViaElement(url);
      } catch (imgError) {
        throw new Error(`Failed to fetch image from ${url}. CORS Error: ${corsError}. Image Load Error: ${imgError}`);
      }
    }

    // Extract proper extension from MIME type
    const extension = mimeType.split("/")[1] || "png";
    const finalFilename = filename.includes(".")
      ? filename
      : `${filename}.${extension}`;

    // Validate blob before creating File
    if (!blob || blob.size === 0) {
      throw new Error(`Invalid blob: size is ${blob?.size || 0} bytes`);
    }

    // Create File with explicit type (CRITICAL for FormData)
    const file = new File([blob], finalFilename, {
      type: mimeType,
      lastModified: Date.now()
    });

    // Final validation

    if (file.size === 0) {
      throw new Error("File created but size is 0 bytes - blob may be corrupted");
    }

    if (!file.type || file.type === 'application/octet-stream') {
    }

    return file;
  } catch (error) {
    throw error;
  }
};

/**
 * Load image via img element and convert to blob using canvas
 * IMPORTANT: Preserves exact image dimensions without any modifications
 *
 * NOTE: This approach has limitations with CORS-protected images:
 * - If the image doesn't have CORS headers, the canvas becomes "tainted"
 * - Tainted canvas cannot export data (toBlob will fail with security error)
 * - Azure Blob Storage needs CORS configuration to allow this approach
 */
const loadImageViaElement = (url: string): Promise<Blob> => {
  return new Promise((resolve, reject) => {

    const tryWithCrossOrigin = (useCORS: boolean) => {
      const img = new Image();

      if (useCORS) {
        img.crossOrigin = "anonymous";
      } else {
      }

      img.onload = () => {
        try {
          const width = img.naturalWidth || img.width;
          const height = img.naturalHeight || img.height;


          const canvas = document.createElement("canvas");
          canvas.width = width;
          canvas.height = height;

          const ctx = canvas.getContext("2d", {
            alpha: true,
            willReadFrequently: false
          });

          if (!ctx) {
            throw new Error("Failed to get canvas context");
          }

          ctx.clearRect(0, 0, width, height);
          ctx.drawImage(img, 0, 0, width, height);


          // Determine output format based on URL extension
          const urlLower = url.toLowerCase();
          let outputFormat = "image/png"; // Default to PNG for safety
          let quality: number | undefined = undefined;

          if (urlLower.endsWith('.jpg') || urlLower.endsWith('.jpeg')) {
            outputFormat = "image/jpeg";
            quality = 1.0; // Maximum quality
          } else if (urlLower.endsWith('.webp')) {
            outputFormat = "image/webp";
            quality = 1.0;
          } else if (urlLower.endsWith('.png')) {
            outputFormat = "image/png";
            // PNG doesn't use quality parameter
          }


          canvas.toBlob(
            (blob) => {
              if (blob) {
                resolve(blob);
              } else {
                throw new Error("Canvas toBlob returned null");
              }
            },
            outputFormat,
            quality
          );
        } catch (canvasError: any) {

          // If we failed without CORS, try with CORS
          if (!useCORS && canvasError.message.includes("tainted")) {
            tryWithCrossOrigin(true);
          } else {
            reject(new Error(`Canvas conversion failed: ${canvasError.message}.
                              This usually means the image server doesn't have CORS enabled.
                              Azure Blob Storage requires CORS configuration to allow downloading images as files.`));
          }
        }
      };

      img.onerror = () => {
        if (!useCORS) {
          tryWithCrossOrigin(true);
        } else {
          reject(new Error(`Failed to load image. The server may not allow cross-origin requests.`));
        }
      };

      const urlWithCache = url.includes("?") ? `${url}&_=${Date.now()}` : `${url}?_=${Date.now()}`;
      img.src = urlWithCache;
    };

    // Start without CORS first (works for same-origin images)
    tryWithCrossOrigin(false);
  });
};

/**
 * Convert a data URL to a File object
 * @param dataURL - Data URL (base64 encoded image)
 * @param filename - Desired filename
 * @returns File
 */
export const dataURLtoFile = (
  dataURL: string,
  filename: string = "image.png"
): File => {
  const arr = dataURL.split(",");
  const mimeMatch = arr[0]?.match(/:(.*?);/);
  const mime = mimeMatch?.[1] ?? "image/png";
  const bstr = atob(arr[1] ?? "");
  let n = bstr.length;
  const u8arr = new Uint8Array(n);

  while (n--) {
    u8arr[n] = bstr.charCodeAt(n);
  }

  return new File([u8arr], filename, { type: mime });
};

/**
 * Convert a File or Blob to data URL
 * @param file - File or Blob to convert
 * @returns Promise<string> - Data URL
 */
export const fileToDataURL = (file: File | Blob): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = () => {
      resolve(reader.result as string);
    };

    reader.onerror = () => {
      reject(new Error("Failed to read file"));
    };

    reader.readAsDataURL(file);
  });
};

/**
 * Convert window location coordinates to points/boxes string format for AI service
 * @param locations - Array of window locations with x, y, width, height
 * @returns JSON string of coordinates: [x1, y1, x2, y2] for single location or [[x1, y1, x2, y2], ...] for multiple
 *
 * Note: This returns rect coordinates which can be used as either 'points' or 'boxes'
 * in the AI API depending on the endpoint requirements.
 * - For segment-replace: Send as 'points' parameter
 * - For other endpoints: May use as 'boxes' parameter
 */
export const locationsToBoxes = (
  locations: {
    x: number;
    y: number;
    width: number;
    height: number;
  }[],
): string => {
  if (!locations || locations.length === 0) {
    return JSON.stringify([]);
  }

  // Convert from {x, y, width, height} to [x1, y1, x2, y2] format
  // IMPORTANT: Round to integers - API doesn't accept floats
  const coordinates = locations.map((loc) => [
    Math.round(loc.x),
    Math.round(loc.y),
    Math.round(loc.x + loc.width),
    Math.round(loc.y + loc.height),
  ]);

  // If only one location, return flat array [x1, y1, x2, y2]
  // Otherwise return nested array [[x1, y1, x2, y2], ...]
  if (coordinates.length === 1) {
    return JSON.stringify([coordinates[0]]);
  }

  return JSON.stringify(coordinates);
};;


/**
 * Build a stable display URL from an AI service result_url.
 *
 * The AI service sometimes returns base64 data URLs (data:image/…) instead
 * of a path. Prepending AI_SERVICE_BASE_URL to a data URL produces a broken
 * URL, so we handle both cases:
 *   - data URL  → decode once into an efficient blob: URL
 *   - path      → prepend AI_SERVICE_BASE_URL as normal
 *
 * Returns a URL that is safe to use in <img src> and in further fetch calls.
 * The caller is responsible for revoking the blob URL (if any) when done.
 */
export const buildResultUrl = (resultUrl: string): string => {
  if (resultUrl.startsWith('data:')) {
    try {
      const [header, b64] = resultUrl.split(',');
      const mime = header?.match(/:(.*?);/)?.[1] || 'image/png';
      const bstr = atob(b64 ?? '');
      const u8arr = new Uint8Array(bstr.length);
      for (let i = 0; i < bstr.length; i++) u8arr[i] = bstr.charCodeAt(i);
      return URL.createObjectURL(new Blob([u8arr], { type: mime }));
    } catch {
      return resultUrl; // fallback: use data URL directly
    }
  }
  return `${APPENV.AI_SERVICE_BASE_URL}${resultUrl}`;
};

export const getProductImageUrl = (answer: Obj): string => {
  if (answer.thumbnail && typeof answer.thumbnail === "string") {
    return answer.thumbnail;
  }
  if (answer.thumbnail?.url) return answer.thumbnail.url;
  return "";
};


export const getProductImageParams = (imageUrl: string): any => {
  const url = new URL(imageUrl);
  const pathParts = url.pathname.split("/").filter((part) => part.length > 0);
  // Find the productdetails segment and extract GUID after it
  const productDetailsIndex = pathParts.findIndex(
    (part) => part === "productdetails",
  );
  const productGuid = pathParts[productDetailsIndex + 1];
  const fileName = pathParts[productDetailsIndex + 2];
  return { productGuid, fileName };
};

/**
 * Parse a blob-storage quote image URL into { quoteGuid, fileName }.
 * URL format: https://<storage>.blob.core.windows.net/quote/<quoteGuid>/image/<fileName>
 */
export const getQuoteImageParams = (imageUrl: string): { quoteGuid: string; fileName: string } | null => {
  try {
    const url = new URL(imageUrl);
    const pathParts = url.pathname.split("/").filter(Boolean);
    // pathname: /quote/<quoteGuid>/image/<fileName>
    const quoteIndex = pathParts.findIndex((p) => p === "quote");
    const imageIndex = pathParts.findIndex((p) => p === "image");
    if (quoteIndex === -1 || imageIndex === -1) return null;
    const quoteGuid = pathParts[quoteIndex + 1];
    const fileName = pathParts.slice(imageIndex + 1).join("/");
    if (!quoteGuid || !fileName) return null;
    return { quoteGuid, fileName };
  } catch {
    return null;
  }
};

/**
 * Fetch a quote image via the backend proxy (avoids direct CORS restrictions on blob storage).
 * Uses the same pattern as fetchProductImageFromBackend — calls GetQuoteImageFile on the backend
 * which fetches from blob storage server-side and returns the raw binary with CORS headers.
 *
 * Backend requirement: implement GetQuoteImageFile Azure Function that accepts
 *   ?quoteGuid=<guid>&fileName=<filename> and returns the raw image bytes from blob storage,
 *   similar to how GetProductImageFile works for product images.
 */
export const fetchQuoteImageFromBackend = async (quoteGuid: string, fileName: string): Promise<File> => {

  // Route through MiddlewareAPI (not a raw fetch) so the Bearer token
  // interceptor in src/lib/axios.ts is applied — the backend now requires it.
  const response = await MiddlewareAPI.get("/GetQuoteImageFile", {
    params: { quoteGuid, fileName },
    responseType: "arraybuffer",
  });

  const arrayBuffer = response.data as ArrayBuffer;
  const contentType = (response.headers["content-type"] as string | undefined) || "image/jpeg";
  const blob = new Blob([arrayBuffer], { type: contentType });
  const ext = fileName.split(".").pop() || "png";
  return new File([blob], `house.${ext}`, { type: contentType });
};
